import { useState, useMemo, useEffect } from 'react';
import { useProductsGrouped } from '../shared/hooks/useProducts';
import { PurchaseOrderCNPJSection } from '../components/PurchaseOrderCNPJSection';
import {
  ShoppingCart, AlertCircle, Package, TrendingUp,
  Loader2, RefreshCw, ChevronsDownUp, ChevronsUpDown, Building2, FileSpreadsheet,
} from 'lucide-react';
import * as XLSX from 'xlsx';

export function PurchaseOrderPage() {
  const { groups, allProducts, loading, error, refetch } = useProductsGrouped();
  const [expandedCNPJs, setExpandedCNPJs] = useState<Set<string>>(new Set());

  const cnpjList = useMemo(() => groups.map((g) => g.cnpj), [groups]);

  useEffect(() => {
    if (groups.length === 1) {
      setExpandedCNPJs(new Set([groups[0].cnpj]));
    } else if (groups.length > 0 && expandedCNPJs.size === 0) {
      setExpandedCNPJs(new Set([groups[0].cnpj]));
    }
  }, [groups]);

  const allExpanded = expandedCNPJs.size === cnpjList.length && cnpjList.length > 0;
  const noneExpanded = expandedCNPJs.size === 0;

  const handleToggle = (cnpj: string) => {
    setExpandedCNPJs((prev) => {
      const next = new Set(prev);
      if (next.has(cnpj)) next.delete(cnpj);
      else next.add(cnpj);
      return next;
    });
  };

  const expandAll = () => setExpandedCNPJs(new Set(cnpjList));
  const collapseAll = () => setExpandedCNPJs(new Set());

  const globalTotals = useMemo(() => {
    const itemsNeedingOrder = allProducts.filter((p) => p.pedidoSugerido > 0);
    const totalItens = itemsNeedingOrder.length;
    const totalUnidades = itemsNeedingOrder.reduce((sum, p) => sum + Math.ceil(p.pedidoSugerido), 0);
    const alta = itemsNeedingOrder.filter(
      (p) => p.status === 'ruptura' || p.status === 'iminente'
    ).length;
    const media = itemsNeedingOrder.filter(
      (p) => p.status === 'minimo' || p.status === 'critica'
    ).length;
    return { totalItens, totalUnidades, alta, media };
  }, [allProducts]);

  const handleExportAllXLSX = () => {
    const wb = XLSX.utils.book_new();

    for (const group of groups) {
      const items = group.products
        .filter((p) => p.pedidoSugerido > 0)
        .map((p) => ({ ean: p.ean, quantidade: Math.ceil(p.pedidoSugerido) }));

      if (items.length === 0) continue;

      const headerRows = [
        ['ATENCAO: Nao remova o cabecalho desta planilha (linhas 1 a 4).'],
        ['Passo 1: Insira um SKU (codigo de barras) por linha e a respectiva quantidade desejada.'],
        ['Passo 2: Salve o arquivo, mantenha o formato original (.xlsx) e importe na plataforma.'],
        ['SKU (codigo de barras)', 'Quantidade desejada'],
      ];
      const dataRows = items.map((i) => [i.ean, i.quantidade]);
      const allRows = [...headerRows, ...dataRows];

      const ws = XLSX.utils.aoa_to_sheet(allRows);
      ws['!cols'] = [{ wch: 25 }, { wch: 22 }];
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 1 } },
      ];

      const digits = group.cnpj.replace(/\D/g, '');
      const sheetName = digits.length > 31 ? digits.slice(0, 31) : digits;
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    }

    if (wb.SheetNames.length > 0) {
      XLSX.writeFile(wb, `pedidos_todos_cnpjs_${new Date().toISOString().slice(0, 10)}.xlsx`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
          <p className="text-gray-500 text-sm">Carregando pedido sugerido...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <p className="text-gray-900 font-semibold">Erro ao carregar dados</p>
          <p className="text-gray-500 text-sm">{error}</p>
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pedido de Compra Sugerido</h1>
          <p className="text-gray-600 mt-2">Carrinho automatico baseado nas necessidades de reposicao</p>
        </div>
        <div className="flex items-center gap-3">
          {groups.length > 1 && globalTotals.totalItens > 0 && (
            <button
              onClick={handleExportAllXLSX}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium shadow-sm"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Gerar Todos (XLSX)
            </button>
          )}
          <button
            onClick={refetch}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 shadow-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Itens para Reposicao</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{globalTotals.totalItens}</p>
            </div>
            <div className="p-3 rounded-full bg-blue-100">
              <Package className="w-6 h-6 text-blue-700" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Unidades</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{globalTotals.totalUnidades}</p>
            </div>
            <div className="p-3 rounded-full bg-teal-100">
              <TrendingUp className="w-6 h-6 text-teal-700" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Urgentes</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{globalTotals.alta}</p>
              <p className="text-xs text-gray-500 mt-1">{globalTotals.media} medias</p>
            </div>
            <div className="p-3 rounded-full bg-red-100">
              <AlertCircle className="w-6 h-6 text-red-700" />
            </div>
          </div>
        </div>
      </div>

      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Building2 className="w-4 h-4 text-gray-400" />
          <span className="font-medium">{groups.length}</span>
          <span>{groups.length === 1 ? 'CNPJ' : 'CNPJs'}</span>
          <span className="text-gray-300 mx-1">|</span>
          <span className="font-medium">{globalTotals.totalItens}</span>
          <span>itens para reposicao</span>
        </div>

        {groups.length > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              disabled={allExpanded}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-white border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm"
            >
              <ChevronsUpDown className="w-4 h-4" />
              Expandir todos
            </button>
            <button
              onClick={collapseAll}
              disabled={noneExpanded}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-white border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm"
            >
              <ChevronsDownUp className="w-4 h-4" />
              Ocultar todos
            </button>
          </div>
        )}
      </div>

      {globalTotals.totalItens === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 border border-gray-200 text-center">
          <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhum pedido necessario</h3>
          <p className="text-gray-600">Todos os produtos estao com estoque adequado no momento.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <PurchaseOrderCNPJSection
              key={group.cnpj}
              cnpj={group.cnpj}
              razaoSocial={group.razaoSocial}
              products={group.products}
              isExpanded={expandedCNPJs.has(group.cnpj)}
              onToggle={() => handleToggle(group.cnpj)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
